import type { UploadFailure, UploadProgress } from './imageUpload'

interface ImageUploadStatusProps {
  uploading: boolean
  progress: UploadProgress | null
  failures: UploadFailure[]
  onRetryFailed: () => void
}

export default function ImageUploadStatus({
  uploading,
  progress,
  failures,
  onRetryFailed,
}: ImageUploadStatusProps) {
  if (!progress && failures.length === 0) {
    return null
  }

  const retryableFailures = failures.filter(failure => failure.retryable)

  return (
    <section className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4" aria-live="polite">
      {progress && (
        <>
          <p className="font-medium text-gray-900">
            {uploading
              ? `Uploading images: ${progress.completed} of ${progress.total} complete`
              : `Upload complete: ${progress.completed} of ${progress.total} processed`}
          </p>
          <p className="mt-1 text-sm text-gray-600">
            {progress.succeeded} succeeded, {progress.failed} failed
          </p>
          <div
            className="mt-3 h-2 overflow-hidden rounded-full bg-gray-200"
            role="progressbar"
            aria-label="Image upload progress"
            aria-valuemin={0}
            aria-valuenow={progress.completed}
            aria-valuemax={progress.total}
          >
            <div
              className="h-full bg-blue-600 transition-all"
              style={{
                width: `${progress.total === 0 ? 0 : (progress.completed / progress.total) * 100}%`,
              }}
            />
          </div>
        </>
      )}

      {failures.length > 0 && (
        <div className="mt-4">
          <h3 className="font-medium text-red-700">Failed images</h3>
          <ul className="mt-2 space-y-2">
            {failures.map((failure, index) => (
              <li
                key={`${failure.file.name}-${index}`}
                className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm"
              >
                <p className="font-medium text-red-800">{failure.file.name}</p>
                <p className="text-red-700">{failure.message}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!uploading && retryableFailures.length > 0 && (
        <button type="button" className="btn-secondary mt-4" onClick={onRetryFailed}>
          Retry failed
        </button>
      )}
    </section>
  )
}
